import { useState, useMemo, useCallback } from "react";
import { Plus, Edit01, Trash01, SearchLg, Play } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Badge } from "@/components/base/badges/badges";
import { usePlaygroundSearch, useEntityTagCounts } from "@/hooks/api/use-rag-playground";
import type { PlaygroundSearchResponse, PlaygroundResult, StrategyOutput } from "@/types/api/rag-playground";
import { Table, TableCard } from "@/components/application/table/table";
import {
    useKnowledgeTags,
    useKnowledgeTagStats,
    useCreateKnowledgeTag,
    useCreateKnowledgeTagGroup,
    useUpdateKnowledgeTag,
    useUpdateKnowledgeTagGroup,
    useDeleteKnowledgeTag,
    useDeleteKnowledgeTagGroup,
    type TagStats,
} from "@/hooks/api/use-knowledge-tags";
import type { KnowledgeTagGroup, KnowledgeTag } from "@/types/api/knowledge-tags";
import type { BadgeColors } from "@/components/base/badges/badge-types";
import { useToast } from "@/hooks/use-toast";

const COLOR_OPTIONS = [
    { id: "gray", label: "Gray" }, { id: "brand", label: "Brand" }, { id: "error", label: "Red" },
    { id: "warning", label: "Orange" }, { id: "success", label: "Green" }, { id: "blue", label: "Blue" },
    { id: "purple", label: "Purple" },
];

const toBadgeColor = (color: string): BadgeColors => {
    const map: Record<string, BadgeColors> = {
        error: "error", warning: "warning", success: "success", brand: "brand",
        blue: "blue", gray: "gray", purple: "purple", orange: "warning", teal: "success",
    };
    return map[color] || "gray";
};

type TabId = "taxonomy" | "assignments" | "playground";

export const TagsAdminPage = () => {
    const toast = useToast();
    const { data: tagGroups = [], isLoading } = useKnowledgeTags();
    const { data: stats } = useKnowledgeTagStats();
    const [activeTab, setActiveTab] = useState<TabId>("taxonomy");

    // Tag manager state
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [showAddGroup, setShowAddGroup] = useState(false);
    const [showAddTag, setShowAddTag] = useState(false);
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [editingTagId, setEditingTagId] = useState<string | null>(null);
    const [groupForm, setGroupForm] = useState({ name: "", label: "", description: "", color: "gray" });
    const [tagForm, setTagForm] = useState({ slug: "", label: "", description: "" });
    const [editGroupForm, setEditGroupForm] = useState({ label: "", description: "", color: "" });
    const [editTagForm, setEditTagForm] = useState({ label: "", description: "" });

    // Mutations
    const createGroup = useCreateKnowledgeTagGroup();
    const createTag = useCreateKnowledgeTag();
    const updateTag = useUpdateKnowledgeTag();
    const updateGroup = useUpdateKnowledgeTagGroup();
    const deleteTag = useDeleteKnowledgeTag();
    const deleteGroup = useDeleteKnowledgeTagGroup();

    const [sortBy] = useState<"slug" | "usage">("usage");
    const [sortDir] = useState<"asc" | "desc">("desc");

    const selectedGroup = tagGroups.find(g => g.group_id === selectedGroupId) || tagGroups[0];
    const tags = useMemo(() => {
        const raw = selectedGroup?.tags || [];
        return [...raw].sort((a, b) => {
            const mul = sortDir === "asc" ? 1 : -1;
            if (sortBy === "usage") return (a.frequency - b.frequency) * mul;
            return a.slug.localeCompare(b.slug) * mul;
        });
    }, [selectedGroup?.tags, sortBy, sortDir]);

    if (!selectedGroupId && tagGroups.length > 0) {
        setSelectedGroupId(tagGroups[0].group_id);
    }

    // Handlers
    const handleCreateGroup = async () => {
        if (!groupForm.name.trim() || !groupForm.label.trim()) return;
        try { await createGroup.mutateAsync(groupForm); toast.success("Group created"); setShowAddGroup(false); setGroupForm({ name: "", label: "", description: "", color: "gray" }); }
        catch { toast.error("Error creating group"); }
    };
    const handleCreateTag = async () => {
        if (!tagForm.slug.trim() || !tagForm.label.trim() || !selectedGroup) return;
        try { await createTag.mutateAsync({ group_id: selectedGroup.group_id, ...tagForm }); toast.success("Tag created"); setShowAddTag(false); setTagForm({ slug: "", label: "", description: "" }); }
        catch { toast.error("Error creating tag"); }
    };
    const handleUpdateGroup = async (groupId: string) => {
        try { await updateGroup.mutateAsync({ groupId, data: editGroupForm }); toast.success("Group updated"); setEditingGroupId(null); }
        catch { toast.error("Error updating group"); }
    };
    const handleUpdateTag = async (tagId: string) => {
        try { await updateTag.mutateAsync({ tagId, data: editTagForm }); toast.success("Tag updated"); setEditingTagId(null); }
        catch { toast.error("Error updating tag"); }
    };
    const handleToggleActive = async (tag: KnowledgeTag) => {
        try { await updateTag.mutateAsync({ tagId: tag.tag_id, data: { is_active: !tag.is_active } }); }
        catch { toast.error("Error toggling tag"); }
    };
    const handleDeleteTag = async (tag: KnowledgeTag) => {
        if (tag.frequency > 0) { toast.error(`Cannot delete: ${tag.frequency} entities use this tag`); return; }
        try { await deleteTag.mutateAsync(tag.tag_id); toast.success("Tag deleted"); }
        catch { toast.error("Error deleting tag"); }
    };
    const handleDeleteGroup = async (group: KnowledgeTagGroup) => {
        if (group.tags && group.tags.length > 0) { toast.error("Remove all tags first"); return; }
        try { await deleteGroup.mutateAsync(group.group_id); toast.success("Group deleted"); if (selectedGroupId === group.group_id) setSelectedGroupId(null); }
        catch { toast.error("Error deleting group"); }
    };

    const tabs: { id: TabId; label: string; count?: number }[] = [
        { id: "taxonomy", label: "Groups & Tags" },
        { id: "assignments", label: "Entity Assignments" },
        { id: "playground", label: "Query Playground" },
    ];

    if (isLoading) {
        return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className="text-xl font-semibold text-primary">Tag Taxonomy</h2>
                <p className="text-tertiary mt-1">Manage tag groups, canonical tags, and test retrieval strategies</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-secondary">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                            activeTab === tab.id
                                ? "border-b-2 border-brand text-brand"
                                : "text-tertiary hover:text-primary"
                        }`}
                    >
                        {tab.label}
                        {tab.count != null && <span className="ml-1.5 opacity-60">{tab.count}</span>}
                    </button>
                ))}
            </div>

            {/* ==================== TAB 1: Groups & Tags ==================== */}
            {activeTab === "taxonomy" && (
                <div className="space-y-6">
                    {/* Groups Panel */}
                    <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-primary">Groups ({tagGroups.length})</h3>
                            <Button onClick={() => setShowAddGroup(!showAddGroup)} size="sm" color="secondary" iconLeading={Plus}>Add</Button>
                        </div>

                        {showAddGroup && (
                            <div className="space-y-2 mb-3 p-3 rounded-lg bg-secondary">
                                <Input placeholder="name (slug)" value={groupForm.name} onChange={(v) => setGroupForm({ ...groupForm, name: v })} size="sm" />
                                <Input placeholder="Label" value={groupForm.label} onChange={(v) => setGroupForm({ ...groupForm, label: v })} size="sm" />
                                <Input placeholder="Description" value={groupForm.description} onChange={(v) => setGroupForm({ ...groupForm, description: v })} size="sm" />
                                <Select placeholder="Color" items={COLOR_OPTIONS} selectedKey={groupForm.color} onSelectionChange={(k) => setGroupForm({ ...groupForm, color: k as string })} size="sm">
                                    {(item) => <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex gap-2">
                                    <Button onClick={handleCreateGroup} size="sm" color="primary" isDisabled={createGroup.isPending}>Save</Button>
                                    <Button onClick={() => setShowAddGroup(false)} size="sm" color="secondary">Cancel</Button>
                                </div>
                            </div>
                        )}

                        {/* Edit group form (shown inline when editing) */}
                        {editingGroupId && (() => {
                            const g = tagGroups.find(gr => gr.group_id === editingGroupId);
                            return g ? (
                                <div className="flex flex-wrap items-end gap-2 mb-3 p-3 rounded-lg bg-secondary">
                                    <Input placeholder="Label" value={editGroupForm.label} onChange={(v) => setEditGroupForm({ ...editGroupForm, label: v })} size="sm" />
                                    <Input placeholder="Description" value={editGroupForm.description} onChange={(v) => setEditGroupForm({ ...editGroupForm, description: v })} size="sm" />
                                    <Select placeholder="Color" items={COLOR_OPTIONS} selectedKey={editGroupForm.color} onSelectionChange={(k) => setEditGroupForm({ ...editGroupForm, color: k as string })} size="sm">
                                        {(item) => <Select.Item key={item.id} id={item.id}>{item.label}</Select.Item>}
                                    </Select>
                                    <Button onClick={() => handleUpdateGroup(editingGroupId)} size="sm" color="primary" isDisabled={updateGroup.isPending}>Save</Button>
                                    <Button onClick={() => setEditingGroupId(null)} size="sm" color="secondary">Cancel</Button>
                                </div>
                            ) : null;
                        })()}

                        {/* Group badges (horizontal) */}
                        <div className="flex flex-wrap gap-2">
                            {tagGroups.map(group => (
                                <button
                                    key={group.group_id}
                                    onClick={() => setSelectedGroupId(group.group_id)}
                                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors ring-1 ring-inset ${
                                        selectedGroupId === group.group_id
                                            ? "bg-secondary ring-brand/30"
                                            : "ring-secondary hover:bg-secondary/50"
                                    }`}
                                >
                                    <Badge color={toBadgeColor(group.color)} size="sm">{group.label}</Badge>
                                    <span className="text-xs text-tertiary">{group.tags?.length || 0}</span>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setEditingGroupId(group.group_id); setEditGroupForm({ label: group.label, description: group.description || "", color: group.color }); }}
                                        className="p-0.5 text-tertiary hover:text-primary"
                                    >
                                        <Edit01 className="size-3" />
                                    </button>
                                    {(!group.tags || group.tags.length === 0) && (
                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group); }} className="p-0.5 text-tertiary hover:text-error">
                                            <Trash01 className="size-3" />
                                        </button>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tags Panel */}
                    <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                        {selectedGroup ? (
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                                            <Badge color={toBadgeColor(selectedGroup.color)} size="sm">{selectedGroup.label}</Badge>
                                            <span className="text-sm text-tertiary font-normal">({tags.length} tags)</span>
                                        </h3>
                                        {selectedGroup.description && <p className="text-xs text-tertiary mt-1">{selectedGroup.description}</p>}
                                    </div>
                                    <Button onClick={() => setShowAddTag(!showAddTag)} size="sm" color="primary" iconLeading={Plus}>Add Tag</Button>
                                </div>

                                {showAddTag && (
                                    <div className="flex items-end gap-2 mb-4 p-3 rounded-lg bg-secondary">
                                        <Input placeholder="slug" value={tagForm.slug} onChange={(v) => setTagForm({ ...tagForm, slug: v })} size="sm" />
                                        <Input placeholder="Label" value={tagForm.label} onChange={(v) => setTagForm({ ...tagForm, label: v })} size="sm" />
                                        <Input placeholder="Description" value={tagForm.description} onChange={(v) => setTagForm({ ...tagForm, description: v })} size="sm" />
                                        <Button onClick={handleCreateTag} size="sm" color="primary" isDisabled={createTag.isPending}>Save</Button>
                                        <Button onClick={() => setShowAddTag(false)} size="sm" color="secondary">Cancel</Button>
                                    </div>
                                )}

                                <TableCard.Root>
                                    <Table>
                                        <Table.Header>
                                            <Table.Head label={`Slug ${sortBy === "slug" ? (sortDir === "asc" ? "↑" : "↓") : ""}`} isRowHeader />
                                            <Table.Head label="Label" />
                                            <Table.Head label="Description" />
                                            <Table.Head label={`Usage ${sortBy === "usage" ? (sortDir === "asc" ? "↑" : "↓") : ""}`} />
                                            <Table.Head label="Active" />
                                            <Table.Head label="Actions" />
                                        </Table.Header>
                                        <Table.Body>
                                            {tags.map(tag => (
                                                <Table.Row key={tag.tag_id}>
                                                    <Table.Cell><span className="text-xs font-mono text-tertiary">{tag.slug}</span></Table.Cell>
                                                    <Table.Cell>
                                                        {editingTagId === tag.tag_id
                                                            ? <Input value={editTagForm.label} onChange={(v) => setEditTagForm({ ...editTagForm, label: v })} size="sm" />
                                                            : <span className="text-sm font-medium text-primary">{tag.label}</span>
                                                        }
                                                    </Table.Cell>
                                                    <Table.Cell>
                                                        {editingTagId === tag.tag_id
                                                            ? <Input value={editTagForm.description} onChange={(v) => setEditTagForm({ ...editTagForm, description: v })} size="sm" />
                                                            : <span className="text-xs text-tertiary">{tag.description || "-"}</span>
                                                        }
                                                    </Table.Cell>
                                                    <Table.Cell><span className="text-sm text-primary">{tag.frequency}</span></Table.Cell>
                                                    <Table.Cell>
                                                        <button onClick={() => handleToggleActive(tag)} className={`rounded-full px-2 py-0.5 text-xs font-medium ${tag.is_active ? "bg-utility-success-50 text-utility-success-700" : "bg-utility-gray-50 text-utility-gray-500"}`}>
                                                            {tag.is_active ? "Active" : "Inactive"}
                                                        </button>
                                                    </Table.Cell>
                                                    <Table.Cell>
                                                        <div className="flex items-center gap-1">
                                                            {editingTagId === tag.tag_id ? (
                                                                <>
                                                                    <Button onClick={() => handleUpdateTag(tag.tag_id)} size="sm" color="primary" isDisabled={updateTag.isPending}>Save</Button>
                                                                    <Button onClick={() => setEditingTagId(null)} size="sm" color="secondary">Cancel</Button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Button onClick={() => { setEditingTagId(tag.tag_id); setEditTagForm({ label: tag.label, description: tag.description || "" }); }} size="sm" color="secondary" iconLeading={Edit01}>Edit</Button>
                                                                    {tag.frequency === 0 && (
                                                                        <Button onClick={() => handleDeleteTag(tag)} size="sm" color="secondary-destructive" iconLeading={Trash01}>Delete</Button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </Table.Cell>
                                                </Table.Row>
                                            ))}
                                        </Table.Body>
                                    </Table>
                                </TableCard.Root>
                            </>
                        ) : (
                            <p className="text-tertiary text-center py-12">Select a group to manage its tags</p>
                        )}
                    </div>
                </div>
            )}

            {/* ==================== TAB 2: Entity Assignments ==================== */}
            {activeTab === "assignments" && <AssignmentsTab stats={stats} tagGroups={tagGroups} toBadgeColor={toBadgeColor} />}

            {/* ==================== TAB 3: Query Playground ==================== */}
            {activeTab === "playground" && <PlaygroundTab />}
        </div>
    );
};


// =============================================================================
// ASSIGNMENTS TAB COMPONENT
// =============================================================================

const ENTITY_BADGE_COLORS: Record<string, BadgeColors> = {
    knowledge_document: "blue",
    issue_episode: "success",
    container_issue: "warning",
    conversation_message: "purple",
    email_message: "indigo",
    document: "orange",
    shipment: "brand",
    contact: "gray",
};

const AssignmentsTab = ({
    stats,
    tagGroups,
    toBadgeColor,
}: {
    stats: TagStats | undefined;
    tagGroups: KnowledgeTagGroup[];
    toBadgeColor: (color: string) => BadgeColors;
}) => {
    const { data: entityCounts = [] } = useEntityTagCounts();

    const activeEntities = entityCounts.filter((e) => e.status === "active");
    const plannedEntities = entityCounts.filter((e) => e.status === "planned");

    return (
        <div className="space-y-6">
            {/* Active entity types — tagged */}
            {activeEntities.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-primary mb-3">Active Sources ({activeEntities.length})</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {activeEntities.map((entity) => (
                            <div key={entity.entity_type} className="rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary">
                                <div className="flex items-center gap-2 mb-1">
                                    <Badge color={ENTITY_BADGE_COLORS[entity.entity_type] || "gray"} size="sm">
                                        {entity.label}
                                    </Badge>
                                </div>
                                <p className="text-xs text-tertiary mb-3">{entity.description}</p>
                                <div className="flex items-baseline gap-4 mb-3">
                                    <div>
                                        <span className="text-2xl font-semibold text-primary">{entity.tagged_entities}</span>
                                        <span className="text-xs text-tertiary ml-1">/ {entity.total_count} entities tagged</span>
                                    </div>
                                    <div>
                                        <span className="text-sm text-quaternary">
                                            ({entity.tagged_count} assignments)
                                        </span>
                                    </div>
                                </div>
                                {/* Coverage bar */}
                                <div className="w-full bg-secondary rounded-full h-1.5">
                                    <div
                                        className="bg-utility-success-500 h-1.5 rounded-full transition-all"
                                        style={{ width: `${Math.min(entity.coverage_pct, 100)}%` }}
                                    />
                                </div>
                                {/* Extractable tags */}
                                <div className="flex flex-wrap gap-1 mt-3">
                                    {entity.extractable_tags.map((tag) => {
                                        const group = tagGroups.find((g) => g.name === tag);
                                        return (
                                            <span
                                                key={tag}
                                                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-secondary text-tertiary"
                                            >
                                                {group?.label || tag}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Planned entity types — not yet tagged */}
            {plannedEntities.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-tertiary mb-3">Planned Sources ({plannedEntities.length})</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {plannedEntities.map((entity) => (
                            <div key={entity.entity_type} className="rounded-lg border border-dashed border-secondary p-4 opacity-60">
                                <div className="flex items-center gap-2 mb-1">
                                    <Badge color="gray" size="sm">{entity.label}</Badge>
                                </div>
                                <p className="text-xs text-tertiary mb-2">{entity.description}</p>
                                <p className="text-xs text-quaternary">{entity.total_count} entities in DB</p>
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {entity.extractable_tags.map((tag) => (
                                        <span
                                            key={tag}
                                            className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium bg-secondary text-quaternary"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Group breakdown table */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <h3 className="text-sm font-semibold text-primary mb-4">Tag Usage by Group</h3>
                <TableCard.Root>
                    <Table>
                        <Table.Header>
                            <Table.Head label="Group" isRowHeader />
                            <Table.Head label="KB Documents" />
                            <Table.Head label="Issue Episodes" />
                            <Table.Head label="Total" />
                        </Table.Header>
                        <Table.Body>
                            {(stats?.group_breakdown || []).map((group) => {
                                const kbDocs = group.by_entity["knowledge_document"] || 0;
                                const episodes = group.by_entity["issue_episode"] || 0;
                                const total = Object.values(group.by_entity).reduce((s, v) => s + v, 0);
                                return (
                                    <Table.Row key={group.name}>
                                        <Table.Cell>
                                            <Badge color={toBadgeColor(group.color)} size="sm">{group.label}</Badge>
                                        </Table.Cell>
                                        <Table.Cell><span className="text-sm text-primary">{kbDocs}</span></Table.Cell>
                                        <Table.Cell><span className="text-sm text-primary">{episodes}</span></Table.Cell>
                                        <Table.Cell><span className="text-sm font-medium text-primary">{total}</span></Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                </TableCard.Root>
            </div>

            {/* Top tags cross-entity */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <h3 className="text-sm font-semibold text-primary mb-4">Top Tags (cross-entity)</h3>
                <TableCard.Root>
                    <Table>
                        <Table.Header>
                            <Table.Head label="Tag" isRowHeader />
                            <Table.Head label="Group" />
                            <Table.Head label="KB Docs" />
                            <Table.Head label="Episodes" />
                            <Table.Head label="Total" />
                        </Table.Header>
                        <Table.Body>
                            {(stats?.top_tags || []).slice(0, 25).map((tag) => (
                                <Table.Row key={tag.slug}>
                                    <Table.Cell><span className="text-sm font-medium text-primary">{tag.label}</span></Table.Cell>
                                    <Table.Cell><Badge color={toBadgeColor(tag.color)} size="sm">{tag.group}</Badge></Table.Cell>
                                    <Table.Cell><span className="text-sm text-primary">{tag.by_entity["knowledge_document"] || 0}</span></Table.Cell>
                                    <Table.Cell><span className="text-sm text-primary">{tag.by_entity["issue_episode"] || 0}</span></Table.Cell>
                                    <Table.Cell><span className="text-sm font-medium text-primary">{tag.total}</span></Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </TableCard.Root>
            </div>
        </div>
    );
};


// =============================================================================
// PLAYGROUND TAB COMPONENT
// =============================================================================

const STRATEGY_CONFIG = [
    { key: "semantic", label: "Semantic Search", desc: "pgvector cosine similarity", color: "brand" as BadgeColors },
    { key: "fulltext", label: "Fulltext Search", desc: "PostgreSQL keyword matching", color: "purple" as BadgeColors },
    { key: "tags", label: "Tag Match", desc: "Canonical tag matching", color: "success" as BadgeColors },
    { key: "glossary", label: "BM25 Glossary", desc: "Keyword fuzzy matching", color: "warning" as BadgeColors },
    { key: "email", label: "Email Search", desc: "Email similarity + recency", color: "blue" as BadgeColors },
];

const ENTITY_TYPE_COLORS: Record<string, BadgeColors> = {
    knowledge_document: "blue",
    issue_episode: "success",
    container_issue: "warning",
    email_message: "purple",
    glossary: "gray",
};

const PlaygroundTab = () => {
    const search = usePlaygroundSearch();
    const [query, setQuery] = useState("");
    const [enabledStrategies, setEnabledStrategies] = useState<Set<string>>(
        new Set(["semantic", "fulltext", "glossary", "email", "tags"]),
    );
    const [topK, setTopK] = useState(5);
    const [minSimilarity, setMinSimilarity] = useState(0.5);
    const [glossaryBoost, setGlossaryBoost] = useState(1.2);
    const [showSettings, setShowSettings] = useState(false);

    const result = search.data as PlaygroundSearchResponse | undefined;

    const toggleStrategy = useCallback((key: string) => {
        setEnabledStrategies((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const handleSearch = useCallback(() => {
        if (!query.trim()) return;
        search.mutate({
            query: query.trim(),
            strategies: Array.from(enabledStrategies),
            top_k: topK,
            min_similarity: minSimilarity,
            glossary_boost: glossaryBoost,
        });
    }, [query, enabledStrategies, topK, minSimilarity, glossaryBoost, search]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSearch();
            }
        },
        [handleSearch],
    );

    return (
        <div className="space-y-4">
            {/* Query bar */}
            <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                <div className="flex gap-3">
                    <div className="flex-1" onKeyDown={handleKeyDown}>
                        <Input
                            placeholder="Enter a query to test retrieval... (e.g. 'phytosanitary certificate issue from Kenya')"
                            value={query}
                            onChange={setQuery}
                            icon={SearchLg}
                            size="md"
                        />
                    </div>
                    <Button
                        onClick={handleSearch}
                        size="md"
                        color="primary"
                        iconLeading={Play}
                        isDisabled={!query.trim() || search.isPending}
                    >
                        {search.isPending ? "Searching..." : "Run"}
                    </Button>
                </div>

                {/* Strategy toggles */}
                <div className="flex items-center gap-4 mt-3">
                    {STRATEGY_CONFIG.map((s) => (
                        <label key={s.key} className="flex items-center gap-1.5 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={enabledStrategies.has(s.key)}
                                onChange={() => toggleStrategy(s.key)}
                                className="rounded border-secondary text-brand focus:ring-brand"
                            />
                            <Badge color={enabledStrategies.has(s.key) ? s.color : "gray"} size="sm">
                                {s.label}
                            </Badge>
                        </label>
                    ))}

                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className="ml-auto text-xs text-tertiary hover:text-primary transition-colors"
                    >
                        {showSettings ? "Hide settings" : "Settings"}
                    </button>
                </div>

                {/* Settings panel */}
                {showSettings && (
                    <div className="flex gap-4 mt-3 pt-3 border-t border-secondary">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-tertiary">top_k</span>
                            <input
                                type="number"
                                value={topK}
                                onChange={(e) => setTopK(Math.max(1, Math.min(50, Number(e.target.value))))}
                                className="w-16 rounded-md border border-secondary bg-primary px-2 py-1 text-sm text-primary"
                                min={1}
                                max={50}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-tertiary">min_similarity</span>
                            <input
                                type="number"
                                step={0.05}
                                value={minSimilarity}
                                onChange={(e) => setMinSimilarity(Math.max(0, Math.min(1, Number(e.target.value))))}
                                className="w-20 rounded-md border border-secondary bg-primary px-2 py-1 text-sm text-primary"
                                min={0}
                                max={1}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-tertiary">glossary_boost</span>
                            <input
                                type="number"
                                step={0.1}
                                value={glossaryBoost}
                                onChange={(e) => setGlossaryBoost(Math.max(0.5, Math.min(3, Number(e.target.value))))}
                                className="w-20 rounded-md border border-secondary bg-primary px-2 py-1 text-sm text-primary"
                                min={0.5}
                                max={3}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Strategy summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {STRATEGY_CONFIG.map((s) => {
                    const output = result?.strategies[s.key] as StrategyOutput | undefined;
                    const isEnabled = enabledStrategies.has(s.key);
                    const hasResults = output && output.count > 0;

                    return (
                        <div
                            key={s.key}
                            className={`rounded-lg border p-4 transition-colors ${
                                hasResults
                                    ? "border-utility-success-300 bg-utility-success-50/30"
                                    : isEnabled
                                      ? "border-secondary"
                                      : "border-secondary opacity-40"
                            }`}
                        >
                            <Badge color={isEnabled ? s.color : "gray"} size="sm">
                                {s.label}
                            </Badge>
                            {output ? (
                                <div className="mt-2 space-y-1">
                                    <div className="text-lg font-semibold text-primary">{output.count}</div>
                                    <div className="text-xs text-tertiary">
                                        top: {output.top_score.toFixed(2)} / avg: {output.avg_score.toFixed(2)}
                                    </div>
                                    <div className="text-xs text-quaternary">{output.time_ms}ms</div>
                                </div>
                            ) : (
                                <p className="text-xs text-tertiary mt-2">{s.desc}</p>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Execution time */}
            {result && (
                <div className="text-xs text-tertiary text-right">
                    Total: {result.execution_time_ms}ms / {result.merged.count} merged results
                </div>
            )}

            {/* Results list */}
            {result && result.merged.count > 0 && (
                <div className="rounded-xl bg-primary shadow-xs ring-1 ring-secondary">
                    <div className="px-4 py-3 border-b border-secondary">
                        <h3 className="text-sm font-semibold text-primary">
                            Merged Results ({result.merged.count})
                        </h3>
                    </div>
                    <div className="divide-y divide-secondary">
                        {result.merged.results.map((r: PlaygroundResult, i: number) => (
                            <div key={`${r.id}-${i}`} className="px-4 py-3 hover:bg-secondary/30 transition-colors">
                                <div className="flex items-start gap-3">
                                    {/* Score */}
                                    <div
                                        className={`shrink-0 w-14 text-center rounded-md px-1.5 py-0.5 text-xs font-mono font-semibold ${
                                            r.score >= 0.8
                                                ? "bg-utility-success-50 text-utility-success-700"
                                                : r.score >= 0.5
                                                  ? "bg-utility-warning-50 text-utility-warning-700"
                                                  : "bg-utility-gray-50 text-utility-gray-600"
                                        }`}
                                    >
                                        {r.score.toFixed(2)}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-medium text-primary truncate">
                                                {r.title}
                                            </span>
                                        </div>
                                        <p className="text-xs text-tertiary line-clamp-2">
                                            {r.content_preview}
                                        </p>
                                        {/* Tags metadata */}
                                        {Array.isArray(r.metadata?.matched_tags) && (
                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                                {r.metadata.matched_tags.map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-utility-brand-50 text-utility-brand-700"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Badges */}
                                    <div className="shrink-0 flex items-center gap-1.5">
                                        <Badge
                                            color={
                                                STRATEGY_CONFIG.find((s) => s.key === r.source_strategy)
                                                    ?.color || "gray"
                                            }
                                            size="sm"
                                        >
                                            {r.source_strategy}
                                        </Badge>
                                        <Badge
                                            color={ENTITY_TYPE_COLORS[r.entity_type] || "gray"}
                                            size="sm"
                                        >
                                            {r.entity_type.replace("_", " ")}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Empty state */}
            {!result && !search.isPending && (
                <div className="rounded-xl bg-primary p-12 shadow-xs ring-1 ring-secondary text-center">
                    <p className="text-sm text-tertiary">
                        Enter a query and click Run to test retrieval strategies
                    </p>
                </div>
            )}

            {/* Loading state */}
            {search.isPending && (
                <div className="rounded-xl bg-primary p-12 shadow-xs ring-1 ring-secondary text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand mx-auto" />
                    <p className="text-sm text-tertiary mt-3">Searching across all strategies...</p>
                </div>
            )}

            {/* No results */}
            {result && result.merged.count === 0 && (
                <div className="rounded-xl bg-primary p-12 shadow-xs ring-1 ring-secondary text-center">
                    <p className="text-sm text-tertiary">No results found for "{result.query}"</p>
                </div>
            )}

            {/* Error */}
            {search.isError && (
                <div className="rounded-xl bg-utility-error-50 p-4 ring-1 ring-utility-error-200">
                    <p className="text-sm text-utility-error-700">
                        Search failed: {(search.error as Error)?.message || "Unknown error"}
                    </p>
                </div>
            )}
        </div>
    );
};
